import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { BlogPost, BlogStatus } from "@tih/shared";
import {
  ApiError,
  adminCreateBlogPost,
  adminFetchBlogPost,
  adminUpdateBlogPost,
} from "../../api/client";
import { AdminImageUploadField } from "../../components/admin/AdminImageUploadField";
import {
  BlogBlockEditor,
  DEFAULT_EDITOR_BODY,
} from "../../components/admin/BlogBlockEditor";

export function AdminBlogEditPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";
  const numericId = isNew ? null : Number(id);
  const presetEventId = Number(searchParams.get("eventId"));
  const linkedEventId =
    isNew && Number.isInteger(presetEventId) && presetEventId > 0 ? presetEventId : null;

  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [body, setBody] = useState(isNew ? DEFAULT_EDITOR_BODY : "");
  const [loadedBody, setLoadedBody] = useState("");
  const [loadedBodyFormat, setLoadedBodyFormat] = useState<"MARKDOWN" | "BLOCKS">("MARKDOWN");
  const [status, setStatus] = useState<BlogStatus>("DRAFT");
  const [eventId, setEventId] = useState<number | null>(linkedEventId);
  const [post, setPost] = useState<BlogPost | null>(null);
  const [editorReady, setEditorReady] = useState(isNew);

  useEffect(() => {
    if (isNew || numericId == null) return;
    setLoading(true);
    adminFetchBlogPost(numericId)
      .then((p) => {
        setPost(p);
        setTitle(p.title);
        setSlug(p.slug);
        setExcerpt(p.excerpt ?? "");
        setCoverUrl(p.coverUrl ?? "");
        setBody(p.body);
        setLoadedBody(p.body);
        setLoadedBodyFormat(p.bodyFormat ?? "MARKDOWN");
        setStatus(p.status);
        setEventId(p.eventId ?? null);
        setEditorReady(true);
      })
      .catch((e) => setError(String((e as Error).message ?? e)))
      .finally(() => setLoading(false));
  }, [isNew, numericId]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const input = {
        title,
        slug: slug.trim() || undefined,
        excerpt: excerpt.trim() || undefined,
        coverUrl: coverUrl.trim() || undefined,
        body: body || loadedBody,
        bodyFormat: "BLOCKS" as const,
        status,
        ...(eventId != null ? { eventId } : { eventId: null }),
      };
      const saved = isNew
        ? await adminCreateBlogPost(input)
        : await adminUpdateBlogPost(numericId!, input);
      if (saved.eventId != null) {
        navigate(`/admin/events/${saved.eventId}/blog`, { replace: true });
      } else {
        navigate(`/admin/blog/${saved.id}`, { replace: true });
      }
      setPost(saved);
      setSlug(saved.slug);
      setBody(saved.body);
      setLoadedBody(saved.body);
      setLoadedBodyFormat(saved.bodyFormat);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Could not save");
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="admin-page" style={{ opacity: 0.5 }}>Loading…</div>;

  return (
    <div className="admin-page">
      <header className="admin-page-header admin-page-header-row">
        <div>
          <h1>{isNew ? "New post" : "Edit post"}</h1>
          {linkedEventId != null && isNew && (
            <p className="admin-muted">This post will be linked to event #{linkedEventId}.</p>
          )}
          {post && (
            <p>
              <code>/blog/{post.slug}</code> · {post.status} · last updated {new Date(post.updatedAt).toLocaleString()}
            </p>
          )}
        </div>
      </header>

      <form onSubmit={onSubmit} className="admin-blog-form">
        <div className="admin-blog-form-fields">
          <label>
            <span>Title</span>
            <input
              type="text"
              required
              maxLength={200}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={busy}
            />
          </label>
          <div className="admin-form-row">
            <label style={{ flex: 2 }}>
              <span>Slug (optional — auto from title)</span>
              <input
                type="text"
                maxLength={200}
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                disabled={busy}
                placeholder="leave blank to auto-generate"
              />
            </label>
            <label style={{ flex: 1 }}>
              <span>Status</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as BlogStatus)}
                disabled={busy}
              >
                <option value="DRAFT">Draft</option>
                <option value="PUBLISHED">Published</option>
              </select>
            </label>
          </div>
          <label>
            <span>Excerpt (optional)</span>
            <input
              type="text"
              maxLength={400}
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              disabled={busy}
            />
          </label>
          <AdminImageUploadField
            label="Cover photo — upload preferred"
            formIdentifier="admin-blog-cover"
            value={coverUrl}
            onChange={setCoverUrl}
            disabled={busy}
            maxUrlLength={500}
          />
        </div>

        <div className="admin-blog-editor">
          <div className="admin-blog-editor-pane admin-blog-editor-pane-full">
            <span>Body</span>
            {editorReady && (
              <BlogBlockEditor
                body={loadedBody}
                bodyFormat={loadedBodyFormat}
                disabled={busy}
                onChange={setBody}
              />
            )}
          </div>
        </div>

        {error && <div className="form-error">{error}</div>}

        <div className="admin-blog-form-actions">
          <button type="button" className="btn btn-ghost" onClick={() => navigate("/admin/blog")}>
            Back
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy || !body}>
            {busy ? "Saving…" : isNew ? "Create post" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
